/**
 * The bid, buy-stats and scope-alternate column builders for the Contracting SOV. Split across three
 * files before, though `contractingSOV.ts` is the only caller of any of them.
 */
import { Bid, BidGroup, BidStats, ScopeAlternate, ScopeAlternateBid } from "../../../interfaces/BidTabs";
import { getNamedRangeFromWorksheet } from "../../workbook";
import { columnNames, rowNames } from "./rangeNames";

/* ---- was bidColumns.ts ---- */

const MAX_BID_COLUMNS = 3;
const FALLBACK_BID_HEADER = "blank";

export type BidColumnInfo = {
  bidIndex: number;
  nameCol: number;
  totalCol: number;
}[];

/**
 * Sort bids: selected first, then by sort_order asc (nulls last), then created_at desc.
 * Returns at most MAX_BID_COLUMNS bids.
 */
const getTopBids = (bidGroup: BidGroup, bids: Bid[] = []): BidStats[] => {
  const bidStats = Object.values(bidGroup.totals).filter((b) => !b.is_carry);

  const bidLookup: Record<string, Bid> = {};
  for (const bid of bids) {
    if (bid.bid_group === bidGroup.id) bidLookup[bid.id] = bid;
  }

  bidStats.sort((a, b) => {
    if (a.selected) return -1;
    if (b.selected) return 1;
    const aOrder = bidLookup[a.id]?.sort_order;
    const bOrder = bidLookup[b.id]?.sort_order;
    if (aOrder != null && bOrder != null && aOrder !== bOrder) return aOrder - bOrder;
    if (aOrder != null && bOrder == null) return -1;
    if (aOrder == null && bOrder != null) return 1;
    const aCreated = bidLookup[a.id]?.created_at || "";
    const bCreated = bidLookup[b.id]?.created_at || "";
    return bCreated.localeCompare(aCreated);
  });
  return bidStats.slice(0, MAX_BID_COLUMNS);
};

/**
 * Dynamically resize the \bids named range to fit up to 3 bids × 2 columns (name + total).
 * Mirrors the pattern in handleUseGroupsWithDenominators.
 *
 * Returns the absolute column index of each bid's name and total columns,
 * plus the max bid count across all bid groups.
 */
export const handleBidColumns = async (
  worksheet: Excel.Worksheet,
  bidGroups: BidGroup[],
  bids: Bid[] = []
): Promise<{ bidColumnInfo: BidColumnInfo; maxBidCount: number } | undefined> => {
  const firstBidColumn = await getNamedRangeFromWorksheet(worksheet, `\\${columnNames.bids}`);
  if (!firstBidColumn) {
    return undefined;
  }

  firstBidColumn.getEntireColumn().ungroup("ByColumns");

  firstBidColumn.load("columnCount");
  const startCell = firstBidColumn.getCell(0, 0);
  startCell.format.load("columnWidth");
  startCell.load("columnIndex");
  await worksheet.context.sync();

  // Determine the max number of bids across all bid groups (capped at MAX_BID_COLUMNS)
  const filteredBidGroups = bidGroups.filter((bg) => !bg.frontend && !bg.qual);
  let maxBidCount = 0;
  for (const bg of filteredBidGroups) {
    const count = getTopBids(bg, bids).length;
    if (count > maxBidCount) maxBidCount = count;
  }
  maxBidCount = Math.min(maxBidCount, MAX_BID_COLUMNS);

  // Each bid gets 2 columns: name + total
  const displayColumns = Math.max(1, maxBidCount * 2);

  // Resize: delete extra columns, then insert new ones
  const existingColumns = firstBidColumn.columnCount;

  if (existingColumns > 1) {
    const toDelete = startCell.getOffsetRange(0, 1).getResizedRange(0, existingColumns - 2);
    toDelete.getEntireColumn().delete(Excel.DeleteShiftDirection.left);
    await worksheet.context.sync();
  }

  if (displayColumns > 1) {
    let newCols = startCell
      .getOffsetRange(0, 1)
      .getResizedRange(0, displayColumns - 2)
      .getEntireColumn();
    newCols.insert(Excel.InsertShiftDirection.right);
    await worksheet.context.sync();

    newCols = startCell
      .getOffsetRange(0, 1)
      .getResizedRange(0, displayColumns - 2)
      .getEntireColumn();
    newCols.copyFrom(startCell.getEntireColumn(), Excel.RangeCopyType.all);
    newCols.format.columnWidth = startCell.format.columnWidth;
  }
  await worksheet.context.sync();

  const updatedRange = startCell.getResizedRange(0, displayColumns - 1);

  // Replace the named range
  worksheet.names.getItemOrNullObject(`\\${columnNames.bids}`).delete();
  await worksheet.context.sync();
  worksheet.names.add(`\\${columnNames.bids}`, updatedRange);

  // Write headers: "Bid 1", "Bid 1 $", "Bid 2", "Bid 2 $", ...
  const headers: string[] =
    maxBidCount > 0
      ? Array.from({ length: maxBidCount }, (_, i) => [`Bid ${i + 1}`, `Bid ${i + 1} $`]).flat()
      : [FALLBACK_BID_HEADER];

  updatedRange.values = [headers];
  await worksheet.context.sync();

  if (maxBidCount === 0) {
    updatedRange.getEntireColumn().clear(Excel.ClearApplyTo.contents);
    await worksheet.context.sync();
  }

  // Build column indices (absolute)
  const bidColumnInfo: BidColumnInfo = [];
  if (maxBidCount > 0) {
    let colIdx = startCell.columnIndex;
    for (let i = 0; i < maxBidCount; i++) {
      bidColumnInfo.push({ bidIndex: i, nameCol: colIdx++, totalCol: colIdx++ });
    }
  }

  updatedRange.getEntireColumn().group("ByColumns");
  await worksheet.context.sync();

  return { bidColumnInfo, maxBidCount };
};

export { getTopBids };

/* ---- was buyStatsColumns.ts ---- */

const BUY_STAT_HEADERS = ["Projected Buy", "Total w/ Proj. Buy", "Buy Value", "Actual Buy"];
const BUY_STAT_COUNT = BUY_STAT_HEADERS.length;

/**
 * Compute buy stats for the selected bidder of a bid group.
 *
 * - projected_buy: percentage rebate = round((buy_value * -1 * 100) / grand_total, 2)
 * - total_with_projected_buy: if awarded → grand_total, else → grand_total + buy_value
 * - buy_value: negotiated savings (from bidGroup or selectedBidder)
 * - actual_buy: post-award savings percentage (only for awarded bids)
 */
export const computeBuyStats = (
  _bidGroup: BidGroup,
  selectedBidder: BidStats | undefined
): { projectedBuy: number; totalWithProjectedBuy: number; buyValue: number; actualBuy: number } => {
  if (!selectedBidder) {
    return { projectedBuy: 0, totalWithProjectedBuy: 0, buyValue: 0, actualBuy: 0 };
  }

  const grandTotal = selectedBidder.grand_total || 0;
  const isAwarded = selectedBidder.award;

  const buyValue = selectedBidder.buy_value || 0;
  const projectedBuy = (selectedBidder.projected_buy || 0) / 100;
  const totalWithProjectedBuy = isAwarded ? grandTotal : grandTotal + buyValue;
  const actualBuy = isAwarded ? (selectedBidder.actual_buy || 0) / 100 : 0;

  return { projectedBuy, totalWithProjectedBuy, buyValue, actualBuy };
};

/**
 * Dynamically resize the \buy_stats named range to fit 4 columns.
 * Mirrors the pattern in handleBidColumns / handleUseGroupsWithDenominators.
 */
export const handleBuyStatsColumns = async (worksheet: Excel.Worksheet): Promise<boolean> => {
  const firstColumn = await getNamedRangeFromWorksheet(worksheet, `\\${columnNames.buyStats}`);
  if (!firstColumn) {
    return false;
  }

  firstColumn.getEntireColumn().ungroup("ByColumns");

  firstColumn.load("columnCount");
  const startCell = firstColumn.getCell(0, 0);
  startCell.format.load("columnWidth");
  startCell.load("columnIndex");
  await worksheet.context.sync();

  const existingColumns = firstColumn.columnCount;
  const displayColumns = BUY_STAT_COUNT;

  if (existingColumns > 1) {
    const toDelete = startCell.getOffsetRange(0, 1).getResizedRange(0, existingColumns - 2);
    toDelete.getEntireColumn().delete(Excel.DeleteShiftDirection.left);
    await worksheet.context.sync();
  }

  if (displayColumns > 1) {
    let newCols = startCell
      .getOffsetRange(0, 1)
      .getResizedRange(0, displayColumns - 2)
      .getEntireColumn();
    newCols.insert(Excel.InsertShiftDirection.right);
    await worksheet.context.sync();

    newCols = startCell
      .getOffsetRange(0, 1)
      .getResizedRange(0, displayColumns - 2)
      .getEntireColumn();
    newCols.copyFrom(startCell.getEntireColumn(), Excel.RangeCopyType.all);
    newCols.format.columnWidth = startCell.format.columnWidth;
  }
  await worksheet.context.sync();

  const updatedRange = startCell.getResizedRange(0, displayColumns - 1);

  // Replace the named range
  worksheet.names.getItemOrNullObject(`\\${columnNames.buyStats}`).delete();
  await worksheet.context.sync();
  worksheet.names.add(`\\${columnNames.buyStats}`, updatedRange);

  // Write headers
  updatedRange.values = [BUY_STAT_HEADERS];

  // Apply number formats per column: Projected Buy (%), Total w/ Proj. Buy ($), Buy Value ($), Actual Buy (%)
  startCell.getOffsetRange(0, 0).getEntireColumn().numberFormat = [["0.00%"]];
  startCell.getOffsetRange(0, 3).getEntireColumn().numberFormat = [["0.00%"]];

  // Apply cell styles so number formats persist on refresh
  startCell.getOffsetRange(1, 1).style = "BuyStatsTotalColumn";
  startCell.getOffsetRange(1, 2).style = "BuyStatsTotalColumn";

  // Apply styles to subtotal, markups first row, and grand total
  const subTotalRange = await getNamedRangeFromWorksheet(worksheet, rowNames.subTotals);
  const grandTotalRange = await getNamedRangeFromWorksheet(worksheet, rowNames.grandTotal);
  const markupsRange = await getNamedRangeFromWorksheet(worksheet, rowNames.contractingMarkups);
  subTotalRange?.load("rowIndex");
  grandTotalRange?.load("rowIndex");
  markupsRange?.load("rowIndex");
  await worksheet.context.sync();

  const col0Abs = startCell.columnIndex;
  const col1Abs = startCell.columnIndex + 1;
  const col2Abs = startCell.columnIndex + 2;
  const col3Abs = startCell.columnIndex + 3;
  for (const rowIndex of [subTotalRange?.rowIndex, grandTotalRange?.rowIndex]) {
    if (rowIndex !== undefined) {
      worksheet.getCell(rowIndex, col1Abs).style = "BuyStatsTotalCell";
      worksheet.getCell(rowIndex, col2Abs).style = "BuyStatsTotalCell";
    }
  }
  // Markups first row — apply column styles so they copy down with new markup rows
  if (markupsRange?.rowIndex !== undefined) {
    const mRow = markupsRange.rowIndex;
    worksheet.getCell(mRow, col0Abs).style = "BuyStatsPercentColumn";
    worksheet.getCell(mRow, col1Abs).style = "BuyStatsTotalColumn";
    worksheet.getCell(mRow, col2Abs).style = "BuyStatsTotalColumn";
    worksheet.getCell(mRow, col3Abs).style = "BuyStatsPercentColumn";
  }
  await worksheet.context.sync();

  updatedRange.getEntireColumn().group("ByColumns");
  await worksheet.context.sync();

  return true;
};

/* ---- was scopeAlternateColumns.ts ---- */

const FALLBACK_HEADER = "blank";

export type AlternateColumnInfo = {
  alternateId: string;
  label: string;
  colIndex: number;
}[];

/**
 * Get unique project-level scope alternates (bid_group is null), sorted by sort_order.
 */
const getUniqueAlternates = (scopeAlternates: ScopeAlternate[]): ScopeAlternate[] => {
  const seen = new Set<string>();
  const unique: ScopeAlternate[] = [];
  const sorted = scopeAlternates.filter((alt) => !alt.bid_group).sort((a, b) => a.sort_order - b.sort_order);
  for (const alt of sorted) {
    if (!seen.has(alt.id)) {
      seen.add(alt.id);
      unique.push(alt);
    }
  }
  return unique;
};

/**
 * Build a lookup: bid_group_id → { alternateId → selected bidder's value }
 */
export const buildAlternateBidLookup = (
  scopeAlternates: ScopeAlternate[],
  scopeAlternateBids: ScopeAlternateBid[],
  selectedBidderIds: Record<string, string>
): Record<string, Record<string, number>> => {
  // scope_alternate_bid: alternate_id → bid_id → value
  const altBidLookup: Record<string, Record<string, number>> = {};
  for (const sab of scopeAlternateBids) {
    if (!altBidLookup[sab.scope_alternate]) altBidLookup[sab.scope_alternate] = {};
    altBidLookup[sab.scope_alternate][sab.bid] = parseFloat(sab.value || "0") || 0;
  }

  // Per bid_group → { alternate_id → value }
  // Only include project-level alternates (bid_group is null)
  const projectAlts = scopeAlternates.filter((alt) => !alt.bid_group);
  const result: Record<string, Record<string, number>> = {};
  for (const alt of projectAlts) {
    // Project-level alternates apply to all bid groups — sum selected bidder's value per bid group
    for (const [bidGroupId, selectedBidderId] of Object.entries(selectedBidderIds)) {
      const value = altBidLookup[alt.id]?.[selectedBidderId] || 0;
      if (!result[bidGroupId]) result[bidGroupId] = {};
      result[bidGroupId][alt.id] = value;
    }
  }
  return result;
};

/**
 * Dynamically resize the \scope_alternates named range.
 * One column per unique scope alternate, headers are alternate labels.
 */
export const handleScopeAlternateColumns = async (
  worksheet: Excel.Worksheet,
  scopeAlternates: ScopeAlternate[]
): Promise<{ alternateColumnInfo: AlternateColumnInfo } | undefined> => {
  const firstColumn = await getNamedRangeFromWorksheet(worksheet, `\\${columnNames.scopeAlternates}`);
  if (!firstColumn) {
    return undefined;
  }

  firstColumn.getEntireColumn().ungroup("ByColumns");

  const uniqueAlts = getUniqueAlternates(scopeAlternates);

  firstColumn.load("columnCount");
  const startCell = firstColumn.getCell(0, 0);
  startCell.format.load("columnWidth");
  startCell.load("columnIndex");
  await worksheet.context.sync();

  const displayColumns = Math.max(1, uniqueAlts.length);
  const existingColumns = firstColumn.columnCount;

  if (existingColumns > 1) {
    const toDelete = startCell.getOffsetRange(0, 1).getResizedRange(0, existingColumns - 2);
    toDelete.getEntireColumn().delete(Excel.DeleteShiftDirection.left);
    await worksheet.context.sync();
  }

  if (displayColumns > 1) {
    let newCols = startCell
      .getOffsetRange(0, 1)
      .getResizedRange(0, displayColumns - 2)
      .getEntireColumn();
    newCols.insert(Excel.InsertShiftDirection.right);
    await worksheet.context.sync();

    newCols = startCell
      .getOffsetRange(0, 1)
      .getResizedRange(0, displayColumns - 2)
      .getEntireColumn();
    newCols.copyFrom(startCell.getEntireColumn(), Excel.RangeCopyType.all);
    newCols.format.columnWidth = startCell.format.columnWidth;
  }
  await worksheet.context.sync();

  const updatedRange = startCell.getResizedRange(0, displayColumns - 1);

  // Replace the named range
  worksheet.names.getItemOrNullObject(`\\${columnNames.scopeAlternates}`).delete();
  await worksheet.context.sync();
  worksheet.names.add(`\\${columnNames.scopeAlternates}`, updatedRange);

  // Write headers
  const headers = uniqueAlts.length > 0 ? uniqueAlts.map((alt) => alt.label) : [FALLBACK_HEADER];

  updatedRange.values = [headers];
  await worksheet.context.sync();

  if (uniqueAlts.length === 0) {
    updatedRange.getEntireColumn().clear(Excel.ClearApplyTo.contents);
    await worksheet.context.sync();
  }

  // Build column info
  const alternateColumnInfo: AlternateColumnInfo = [];
  if (uniqueAlts.length > 0) {
    let colIdx = startCell.columnIndex;
    for (const alt of uniqueAlts) {
      alternateColumnInfo.push({ alternateId: alt.id, label: alt.label, colIndex: colIdx++ });
    }
  }

  updatedRange.getEntireColumn().group("ByColumns");
  await worksheet.context.sync();

  return { alternateColumnInfo };
};
